import { FC } from 'react'

import _ from 'lodash'

import PeopleList from 'pages/people/PeopleList'
import {
  Panels,
  ListNav2,
  ModelViewer,
  LoadingContainer,
  Input,
  Modal,
  LegacyPage,
  Model,
} from 'peach/components'
// import { useVerificationTypesQuery } from 'peach/data'
import { getName } from 'peach/helpers'
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useParams,
  ListSubroutes,
} from 'peach/router'

import { useBetaPages } from 'core/beta'
import { enginePilotSlugs } from 'core/resources'
import { useCompanyPermissionsGet } from 'core/types/generated/api-internal/hooks'
import {
  useCompanyGet,
  useUpdateCompany,
} from 'core/types/generated/company/hooks'

import CaseType from './CaseTypes/CaseType'
import CaseTypes from './CaseTypes/CaseTypes'
import CompanyBootstrap from './CompanyBootstrap'
import SupercasesRoutes from './Supercases/SupercasesRoutes'
import CompanyConfig from './CompanyConfig'
import CompanyImportTemplates from './CompanyImportTemplates'
import CompanyInvestors from './CompanyInvestors'
import CompanyEmployeesRoutes from './employees/CompanyEmployeesRoutes'
import enginePilotNav from './enginePilotNav'
import LoanTypesRoutes from './LoanTypes/LoanTypesRoutes'
import LoanTapeTypesRoutes from './LoanTapeTypes/LoanTapeTypesRoutes'
import LoanTapesRoutes from './LoanTapes/LoanTapesRoutes'
import CompanyRoles from './roles/CompanyRoles'
import CompanyTeams from './teams/CompanyTeams'
import CompanyDescriptor from './templates/CompanyDescriptor'
import CompanyDescriptors from './templates/CompanyDescriptors'
import CompanyTemplatesRoutes from './templates/CompanyTemplatesRoutes'
import TemplateSubjectRoutes from './templates/subjects/TemplateSubjectRoutes'
import CompanyUsersRoutes from './users/CompanyUsersRoutes'
import WorkflowStepTypesRoutes from './workflowStepTypes/WorkflowStepTypesRoutes'
import WorkflowTypesRoutes from './workflowTypes/WorkflowTypesRoutes'
import WorkflowItemTypesRoutes from './workflowItemTypes/WorkflowItemTypesRoutes'
import AppDomainsRoutes from './AppDomains/AppDomainsRoutes'
import APIKeysRoutes from './APIKeys/APIKeysRoutes'
import CreditAgenciesRoutes from './CreditAgencies/CreditAgenciesRoutes'
import MonitoringServicesRoutes from './MonitoringServices/MonitoringServicesRoutes'
import EnginePilotRoutes from './EnginePilots'

const Company: FC = () => {
  const { path, url } = useRouteMatch()
  const { companyId } = useParams()
  const { betaPages } = useBetaPages()

  const { data: company, isLoading: isLoadingCompany } = useCompanyGet({
    companyId,
  })

  // const [verificationTypes] = useVerificationTypesQuery()

  const { data: permissions, isLoading: isLoadingPermissions } =
    useCompanyPermissionsGet({
      companyId,
    })

  const { mutateAsync: updateCompany, isPending: isPendingCompany } =
    useUpdateCompany({ companyId })

  const updateCompanyModal = (
    <Modal
      initialValue={_.pick(company, ['legalName', 'domainName'])}
      onSubmit={async (values) => await updateCompany(values)}
      title='Edit Company'
      trigger
    >
      <Input formKey='legalName' />
      <Input formKey='domainName' />
    </Modal>
  )

  const baseItems = [
    [getName(company) || 'Company', '', true],
    '/bootstrap',
    '/all-permissions',
    '/roles',
    '/config',
    '/app-domains',
    '/api-keys',
    '/credit-agencies',
    '/monitoring/services',
    {
      label: 'Types',
      items: [
        '/loan-types',
        '/case-types',
        '/workflow-types',
        '/workflow-step-types',
        '/workflow-item-types',
        '/loan-tape-types',
        // '/verification-types',
      ],
    },
    {
      label: 'Records',
      items: [
        '/borrowers',
        '/users',
        '/employees',
        '/teams',
        '/investors',
        '/supercases',
        '/loan-tapes',
      ],
    },
    {
      label: 'Templates',
      items: ['/subjects', '/templates', '/descriptors', '/import-templates'],
    },
  ]

  const items = betaPages ? enginePilotNav(baseItems) : baseItems

  const nav = <ListNav2 baseUrl={url} items={items} searchable />

  const { config, ...restCompany } = company || {}

  return (
    <Panels left={nav}>
      <LoadingContainer loading={isLoadingCompany || isPendingCompany}>
        <Switch>
          {betaPages && (
            <Route
              crumb
              path={enginePilotSlugs.map((slug) => `${path}/${slug}`)}
            >
              <EnginePilotRoutes />
            </Route>
          )}

          <Route exact path={`${path}`}>
            <LegacyPage.Full>
              {updateCompanyModal}
              <ModelViewer json={restCompany} />
            </LegacyPage.Full>
          </Route>

          <Route crumb exact path={`${path}/bootstrap`}>
            <CompanyBootstrap />
          </Route>

          <Route crumb exact path={`${path}/config`}>
            <CompanyConfig />
          </Route>

          <Route crumb path={`${path}/api-keys`}>
            <APIKeysRoutes />
          </Route>

          <Route crumb path={`${path}/roles`}>
            <CompanyRoles />
          </Route>

          <Route crumb path={`${path}/all-permissions`}>
            <LoadingContainer loading={isLoadingPermissions}>
              <LegacyPage.Full>
                <Model.Item json={permissions} title='All Permissions' />
              </LegacyPage.Full>
            </LoadingContainer>
          </Route>

          <Route crumb path={`${path}/loan-types`}>
            <LoanTypesRoutes />
          </Route>

          <Route crumb path={`${path}/loan-tape-types`}>
            <LoanTapeTypesRoutes />
          </Route>

          <Route crumb path={`${path}/case-types`}>
            <ListSubroutes
              item={<CaseType />}
              list={<CaseTypes />}
              paramKey='caseTypeId'
            />
          </Route>

          <Route crumb path={`${path}/supercases`}>
            <SupercasesRoutes />
          </Route>

          <Route crumb path={`${path}/workflow-types`}>
            <WorkflowTypesRoutes />
          </Route>

          {/* We currently don't have this endpoint listed in our API docs. Please request BE to add it before using this feature. */}
          {/* <Route path={`${path}/verification-types`} crumb>
            <ListSubroutes
              title='Verification Types'
              paramKey='verificationTypeId'
              json={verificationTypes}
              page
            />
          </Route> */}

          <Route crumb path={`${path}/workflow-step-types`}>
            <WorkflowStepTypesRoutes />
          </Route>

          <Route crumb path={`${path}/workflow-item-types`}>
            <WorkflowItemTypesRoutes />
          </Route>

          <Route crumb path={`${path}/investors`}>
            <CompanyInvestors />
          </Route>

          <Route crumb exact path={`${path}/borrowers`}>
            <PeopleList />
          </Route>

          <Route crumb path={`${path}/employees`}>
            <CompanyEmployeesRoutes />
          </Route>

          <Route crumb path={`${path}/teams`}>
            <CompanyTeams />
          </Route>

          <Route crumb path={`${path}/users`}>
            <CompanyUsersRoutes />
          </Route>

          <Route crumb path={`${path}/loan-tapes`}>
            <LoanTapesRoutes />
          </Route>

          <Route crumb path={`${path}/templates`}>
            <CompanyTemplatesRoutes />
          </Route>

          <Route crumb path={`${path}/subjects`}>
            <TemplateSubjectRoutes />
          </Route>

          <Route crumb path={`${path}/descriptors`}>
            <ListSubroutes
              item={<CompanyDescriptor />}
              list={<CompanyDescriptors />}
              page
              paramKey='descriptorId'
              title='Template Descriptors'
            />
          </Route>

          <Route crumb path={`${path}/import-templates`}>
            <CompanyImportTemplates />
          </Route>

          <Route crumb path={`${path}/app-domains`}>
            <AppDomainsRoutes />
          </Route>

          <Route crumb path={`${path}/credit-agencies`}>
            <CreditAgenciesRoutes />
          </Route>

          <Route crumb path={`${path}/monitoring/services`}>
            <MonitoringServicesRoutes />
          </Route>

          <Redirect to={`${path}`} />
        </Switch>
      </LoadingContainer>
    </Panels>
  )
}

export default Company
